import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Package, ShoppingCart, Loader2, Layers, ChevronRight, ChevronLeft, ZoomIn, X, ExternalLink } from "lucide-react";

const CART_KEY = "droneops_cart";
const imgUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;

function flattenTree(items, map, depth, pathKey, acc) {
  for (const s of items) {
    const p = map[s.part_id];
    if (!p) continue;
    const key = `${pathKey}/${s.part_id}`;
    acc.push({ key, part: p, qty: s.qty, depth });
    const kids = p.sub_parts || [];
    if (kids.length && depth < 8 && !pathKey.includes(s.part_id)) flattenTree(kids, map, depth + 1, key, acc);
  }
  return acc;
}

export default function PartDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoverPart, setHoverPart] = useState(null);
  const [zoomImg, setZoomImg] = useState(null);
  const [activeImg, setActiveImg] = useState(null);
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 5);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };
  const scrollStrip = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };
  useEffect(() => { setActiveImg(null); }, [id]);
  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [parts, id]);

  useEffect(() => {
    api.get("/parts").then((r) => setParts(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  const map = useMemo(() => Object.fromEntries(parts.map((p) => [p.id, p])), [parts]);
  const part = map[id];

  // On hover-capable (desktop) devices a click opens the sub-part immediately.
  // On touch devices (no hover) the first tap just highlights/identifies the marker,
  // and a second tap on the same one opens it.
  const canHover = useMemo(() => window.matchMedia?.("(hover: hover)")?.matches ?? true, []);
  const activatePart = (partId) => {
    if (!map[partId]) return;
    if (canHover || hoverPart === partId) {
      navigate(`/parts/${partId}`);
    } else {
      setHoverPart(partId);
    }
  };

  const addToCart = (p) => {
    try {
      const cart = JSON.parse(sessionStorage.getItem(CART_KEY)) || {};
      cart[p.id] = (cart[p.id] || 0) + 1;
      sessionStorage.setItem(CART_KEY, JSON.stringify(cart));
      window.dispatchEvent(new Event("cart-updated"));
      toast.success(`Added ${p.name} to cart`);
    } catch { toast.error("Could not add to cart"); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;
  if (!part) return <div className="py-24 text-center text-slate-500">Part not found. <button onClick={() => navigate("/parts")} className="text-lime-600 underline">Back to catalog</button></div>;

  const tree = flattenTree(part.sub_parts || [], map, 0, "", []);
  const pinByPart = {};
  (part.hotspots || []).forEach((h, i) => { if (!(h.part_id in pinByPart)) pinByPart[h.part_id] = i + 1; });
  const gallery = [part.image_path, ...(part.images || [])].filter(Boolean);
  const activeSrc = activeImg || part.image_path;

  return (
    <div className="space-y-6 fade-up" data-testid="part-detail-page">
      <button onClick={() => navigate("/parts")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="part-back-btn"><ArrowLeft className="h-4 w-4" /> Parts catalog</button>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {/* Left: details + diagram */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="part-images">
            <div className="flex gap-4 items-stretch">
              {/* Hero image (left) */}
              <div className="h-56 w-56 shrink-0 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden relative group">
                {activeSrc ? (
                  <>
                    <button type="button" onClick={() => setZoomImg(activeSrc)} className="h-full w-full flex items-center justify-center cursor-zoom-in" data-testid="hero-zoom-btn">
                      <img src={imgUrl(activeSrc)} alt={part.name} className="h-full w-full object-contain" />
                    </button>
                    <span className="absolute top-2 right-2 h-8 w-8 rounded-full bg-slate-900/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"><ZoomIn className="h-4 w-4" /></span>
                  </>
                ) : <Package className="h-16 w-16 text-slate-300" />}
              </div>

              {/* Additional photos (scroll across to the right) */}
              {gallery.length > 1 && (
                <div className="relative flex-1 min-w-0 self-center">
                  {canLeft && (
                    <button type="button" onClick={() => scrollStrip(-1)} data-testid="gallery-scroll-left"
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:text-slate-900">
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                  )}
                  <div ref={scrollRef} onScroll={updateArrows} data-testid="part-gallery"
                    className="flex gap-2 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
                    {gallery.map((p) => (
                      <button key={p} type="button" onClick={() => setActiveImg(p)} data-testid="part-gallery-thumb"
                        className={`h-56 w-40 shrink-0 rounded-lg border overflow-hidden transition ${activeSrc === p ? "border-lime-400 ring-2 ring-lime-400" : "border-slate-200 hover:border-slate-300"}`}>
                        <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                  {canRight && (
                    <button type="button" onClick={() => scrollStrip(1)} data-testid="gallery-scroll-right"
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:text-slate-900">
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl">
            <div className="p-6">
              <span className="text-[10px] font-bold uppercase tracking-wider text-lime-600">{part.category}</span>
              <h1 className="text-2xl font-bold font-heading text-slate-900 mt-0.5">{part.name}</h1>
              {part.sku && <p className="text-sm text-slate-400 mt-1">SKU: {part.sku}</p>}
              {part.description && <p className="text-slate-600 mt-3">{part.description}</p>}
              {part.compatible_with && <p className="text-sm text-slate-500 mt-2">Fits: {part.compatible_with}</p>}
              <div className="flex items-center gap-4 mt-4">
                <span className="text-2xl font-bold font-heading text-slate-900">${part.price.toFixed(2)}</span>
                <span className={`text-sm font-medium ${part.stock > 5 ? "text-lime-600" : part.stock > 0 ? "text-amber-600" : "text-red-600"}`}>{part.stock > 0 ? `${part.stock} in stock` : "Out of stock"}</span>
              </div>
              <Button data-testid="part-add-cart-btn" onClick={() => addToCart(part)} disabled={part.stock <= 0} className="mt-4 bg-slate-900 hover:bg-slate-800 text-white"><ShoppingCart className="h-4 w-4 mr-1.5" /> Add to cart</Button>
            </div>
          </div>

          {part.image_path && part.hotspots?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-semibold text-slate-900">Diagram</h2>
                <button type="button" onClick={() => setZoomImg(part.image_path)} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900" data-testid="diagram-zoom-btn"><ZoomIn className="h-3.5 w-3.5" /> Enlarge</button>
              </div>
              <div className="relative rounded-xl overflow-hidden bg-slate-100 inline-block max-w-full align-top">
                <img src={imgUrl(part.image_path)} alt="" className="block w-auto max-w-full max-h-[400px]" />
                {part.hotspots.map((h, i) => {
                  const active = hoverPart === h.part_id;
                  return (
                    <button key={h.id} type="button"
                      onMouseEnter={() => setHoverPart(h.part_id)} onMouseLeave={() => setHoverPart(null)}
                      onClick={() => activatePart(h.part_id)}
                      data-testid={`hotspot-dot-${h.part_id}`}
                      style={{ left: `${h.x}%`, top: `${h.y}%` }} className="absolute -translate-x-1/2 -translate-y-full cursor-pointer">
                      <span className={`h-6 w-6 rounded-full text-xs font-bold flex items-center justify-center shadow-md border-2 border-white transition-transform ${active ? "bg-lime-400 text-slate-900 scale-125" : "bg-slate-900 text-white"}`}>{i + 1}</span>
                    </button>
                  );
                })}
              </div>
              {!canHover && (
                <p className="text-[11px] text-slate-400 mt-2 text-center" data-testid="diagram-touch-hint">Tap a marker to identify it, tap again to open the part.</p>
              )}
            </div>
          )}
        </div>

        {/* Right: sub-part tree */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:sticky lg:top-24">
          <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-1"><Layers className="h-5 w-5 text-lime-500" /> Sub-part tree</h2>
          <p className="text-sm text-slate-500 mb-4">{tree.length} components{part.sub_parts?.length ? "" : " — this is a standalone part"}.</p>
          {tree.length === 0 ? (
            <p className="text-sm text-slate-400">No sub-parts.</p>
          ) : (
            <div className="border border-slate-100 rounded-lg py-1" data-testid="part-tree">
              {tree.map((row) => {
                const pin = pinByPart[row.part.id];
                const active = hoverPart === row.part.id;
                return (
                  <button key={row.key} onClick={() => activatePart(row.part.id)} data-testid={`tree-part-${row.part.id}`}
                    onMouseEnter={() => setHoverPart(row.part.id)} onMouseLeave={() => setHoverPart(null)}
                    className={`w-full flex items-center gap-2 py-1.5 pr-2 rounded-lg text-left transition-colors ${active ? "bg-lime-50 ring-1 ring-lime-300" : "hover:bg-slate-50"}`} style={{ paddingLeft: `${8 + row.depth * 18}px` }}>
                    {pin ? (
                      <span className={`h-5 w-5 shrink-0 rounded-full text-[11px] font-bold flex items-center justify-center border-2 border-white shadow-sm ${active ? "bg-lime-400 text-slate-900" : "bg-slate-900 text-white"}`}>{pin}</span>
                    ) : (
                      <span className="h-5 w-5 shrink-0 rounded-full border border-dashed border-slate-200" />
                    )}
                    <div className="h-6 w-6 rounded bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                      {row.part.image_path ? <img src={imgUrl(row.part.image_path)} alt="" className="h-full w-full object-cover" /> : <Package className="h-3 w-3 text-slate-300" />}
                    </div>
                    <span className="text-sm text-slate-800 truncate flex-1">{row.part.name}</span>
                    {row.qty > 1 && <Badge variant="outline" className="text-[10px] border-slate-300">×{row.qty}</Badge>}
                    {row.part.sub_parts?.length > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {zoomImg && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 sm:p-8" onClick={() => setZoomImg(null)} data-testid="image-lightbox">
          <img src={imgUrl(zoomImg)} alt={part.name} className="max-h-full max-w-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setZoomImg(null)} className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="lightbox-close"><X className="h-5 w-5" /></button>
          <a href={imgUrl(zoomImg)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white underline" data-testid="lightbox-newtab"><ExternalLink className="h-3.5 w-3.5" /> Open in new tab</a>
        </div>
      )}
    </div>
  );
}
